import React from 'react';
import { Trophy, Target, Users, Zap } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { teamRows } from '../data/mock';

const TeamSection = () => {
  const { t } = useTranslation();

  return (
    <section id="team" className="section-spacing bg-page">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <Users className="text-primary" size={32} />
            <span className="caption text-primary">{t('team.subtitle')}</span>
          </div>
          <h2 className="heading-1 text-primary mb-6">
            {t('team.title')}
          </h2>
          <p className="body-large text-secondary max-w-3xl mx-auto">
            {t('team.description')}
          </p>
        </div>

        {/* Team Rows */}
        {teamRows.map((row, rowIndex) => (
          <div key={rowIndex} className="mb-12">
            <h3 className="heading-2 text-primary mb-6">{row.label}</h3>
            <div id={rowIndex === 0 ? "member" : undefined} className="rounded-lg p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {row.members.map((member, index) => (
                  <div key={index} className="card-hover-effect rounded-lg overflow-hidden group">
                    {/* Player Image */}
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-50 transition-all duration-300"></div>

                      {/* Rank Badge */}
                      {member.rank && (
                        <div className="absolute top-4 right-4">
                          <div className="bg-grey-800 text-inverse px-3 py-1 rounded-full">
                            <span className="caption">{member.rank}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Player Info */}
                    <div className="p-6 space-y-4 bg-gray-800 transition-colors group-hover:bg-[var(--border-medium)]">
                      <div>
                        <h3 className="text-xl font-bold text-primary mb-2">{member.name}</h3>
                        <p className="body-medium text-secondary">{member.role}</p>
                        <p className="caption text-muted">{member.specialization}</p>
                      </div>

                      {/* Achievements */}
                      <div className="space-y-2">
                        {member.achievements.map((achievement, achIndex) => (
                          <div key={achIndex} className="flex items-center space-x-2">
                            <Trophy size={16} className="text-yellow-400" />
                            <span className="body-medium text-secondary">{achievement}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Team Stats Summary */}
        <div className=" bg-gray-800 mt-16 bg-card rounded-lg p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <Target className="text-primary mx-auto mb-3" size={32} />
              <div className="heading-2 text-primary">{t('team.stats.battlefield')}</div>
              <div className="caption">{t('team.stats.battlefield_desc')}</div>
            </div>
            <div className="text-center">
              <Trophy className="text-yellow-400 mx-auto mb-3" size={32} />
              <div className="heading-2 text-primary">{t('team.stats.achievements')}</div>
              <div className="caption">{t('team.stats.achievements_desc')}</div>
            </div>
            <div className="text-center">
              <Users className="text-green-400 mx-auto mb-3" size={32} />
              <div className="heading-2 text-primary">{t('team.stats.members')}</div>
              <div className="caption">{t('team.stats.members_desc')}</div>
            </div>
            <div className="text-center">
              <Zap className="text-blue-400 mx-auto mb-3" size={32} />
              <div className="heading-2 text-primary">{t('team.stats.winrate')}</div>
              <div className="caption">Avg Win Rate</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;